import { useEffect, useRef, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { FileText, Film, Images, Loader2, X } from "lucide-react";

import type { FilesPage } from "@/api/db/messages";
import type { FileFilter } from "@/api/router/cockpit/schemas";
import { orpc } from "@/client";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { OverlayShell } from "@/components/ui/overlay";
import { CockpitLabels } from "@/lib/labels";
import { djs } from "@/lib/djs";
import { cn } from "@/lib/utils";

type FilesCursor = FilesPage["next_before"];

type FileItem = FilesPage["items"][number];

function FileTile({ item }: { item: FileItem }) {
	const url = `/media/${encodeURIComponent(item.id)}`;
	const [imageBroken, setImageBroken] = useState(false);

	const label = item.media_filename ?? CockpitLabels.files.type[item.type];
	const showImage = item.type === "image" && !imageBroken;
	const Icon = item.type === "video" ? Film : item.type === "image" ? Images : FileText;

	return (
		<a
			href={url}
			target="_blank"
			rel="noreferrer"
			data-slot="file-tile"
			data-type={item.type}
			data-direction={item.direction}
			className="surface-panel group flex flex-col overflow-hidden transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
		>
			<div className="flex aspect-square items-center justify-center overflow-hidden bg-muted">
				{showImage && (
					<img
						src={url}
						loading="lazy"
						alt=""
						onError={() => setImageBroken(true)}
						className="size-full object-cover transition-transform group-hover:scale-105"
					/>
				)}
				{!showImage && <Icon className="size-8 text-muted-foreground" />}
			</div>

			<div className="flex flex-col gap-0.5 px-2 py-1.5">
				<p className="truncate text-xs font-medium text-foreground" title={label}>
					{label}
				</p>

				<div className="flex items-center justify-between gap-2 font-mono text-xs text-muted-foreground">
					<span>{djs(item.sent_at).format("DD/MM/YYYY")}</span>
					<span>{CockpitLabels.files.direction[item.direction]}</span>
				</div>
			</div>
		</a>
	);
}

export function FilesGallery({ personId, onClose }: { personId: number; onClose: () => void }) {
	const [filter, setFilter] = useState<FileFilter>("all");
	const sentinelRef = useRef<HTMLDivElement>(null);

	const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery(
		orpc.cockpit.files.infiniteOptions({
			input: (before: FilesCursor) => ({ person_id: personId, type: filter, before }),
			initialPageParam: null as FilesCursor,
			getNextPageParam: (lastPage) => lastPage.next_before,
		}),
	);

	useEffect(() => {
		const el = sentinelRef.current;

		if (el === null) {
			return;
		}

		const observer = new IntersectionObserver((entries) => {
			const entry = entries.at(0);

			if (!entry) {
				return;
			}

			if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
				fetchNextPage();
			}
		});

		observer.observe(el);

		return () => observer.disconnect();
	}, [hasNextPage, isFetchingNextPage, fetchNextPage]);

	const items = data?.pages.flatMap((page) => page.items) ?? [];

	return (
		<OverlayShell
			label={CockpitLabels.files.title}
			onClose={onClose}
			className="items-center justify-center p-4"
		>
			<section data-component="files-gallery" className="overlay-panel max-w-4xl overflow-hidden">
				<header className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
					<h2 className="type-section-title text-foreground">{CockpitLabels.files.title}</h2>
					<Button variant="ghost" size="icon" aria-label="Fechar" onClick={onClose}>
						<X />
					</Button>
				</header>

				<div className="flex flex-wrap gap-1 border-b border-border px-5 py-3">
					{CockpitLabels.files.filters.map((option) => (
						<button
							key={option.value}
							type="button"
							data-slot="files-filter"
							data-active={option.value === filter}
							aria-pressed={option.value === filter}
							onClick={() => setFilter(option.value)}
							className={cn(
								"rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
								option.value === filter
									? "border-primary/40 bg-primary/10 text-primary"
									: "border-border text-muted-foreground hover:text-foreground",
							)}
						>
							{option.label}
						</button>
					))}
				</div>

				<div className="overflow-y-auto p-5">
					{isLoading && (
						<div className="flex justify-center py-12">
							<Loader2 className="size-5 animate-spin text-muted-foreground" />
						</div>
					)}

					{!isLoading && items.length === 0 && (
						<EmptyState icon={Images} title={CockpitLabels.files.empty[filter]} />
					)}

					{items.length > 0 && (
						<ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
							{items.map((item) => (
								<li key={item.id}>
									<FileTile item={item} />
								</li>
							))}
						</ul>
					)}

					<div ref={sentinelRef} aria-hidden="true" className="h-px" />

					{isFetchingNextPage && (
						<div className="flex justify-center py-3">
							<Loader2 className="size-4 animate-spin text-muted-foreground" />
						</div>
					)}
				</div>
			</section>
		</OverlayShell>
	);
}
